import {
  HasuraDataTableColumnDef,
  DataTableEx,
  DataTableExProps,
  DataTableExRef,
  FormInput,
  AutocompleteInput,
  FileInput,
  CheckboxInput,
  FormGetter,
  ConditionalInput,
  SelectInput,
  FormTabs,
  FormTab,
  FormSetter,
  HiddenInput,
  Grid,
  SubmitButton,
  AutocompleteInputProps,
} from '@kirz/mui-admin';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { ShapeOutline, ImageOutline, Plus } from 'mdi-material-ui';
import React, {
  forwardRef,
  Fragment,
  Ref,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm, useFormContext } from 'react-hook-form';

import {
  TEMPLATE_TYPE_ITEMS,
  TEMPLATE_MODEL_ITEMS,
  TEMPLATE_DURATION_ITEMS,
  TEMPLATE_RATIO_ITEMS,
} from 'constants/template';

import { TemplateImagesTable } from './TemplateImagesTable';

export const TemplatesTable = forwardRef(
  (
    props: Partial<DataTableExProps> & {
      id: string;
      filterColumns?: (
        columns: HasuraDataTableColumnDef[],
      ) => HasuraDataTableColumnDef[];
    },
    ref: Ref<DataTableExRef>,
  ) => {
    const [selectedType, setSelectedType] = useState<'image' | 'video'>(
      'image',
    );
    const [selectedTemplateId, setSelectedTemplateId] = useState<
      number | undefined
    >(undefined);
    const { filterColumns, ...rest } = props;
    const tableRef = useRef<DataTableExRef>(null);
    const imagesTableRef = useRef<DataTableExRef>(null);
    const openIdAfterSave = useRef<number | null>(null);
    const needToOpenFormAfterSave = useRef(false);

    useImperativeHandle(
      ref,
      () =>
        ({
          openNewTemplateForm: (type: 'image' | 'video') => {
            setSelectedType(type);
            setSelectedTemplateId(undefined);
            tableRef.current?.openFormDialog();
          },
          // ...tableRef.current!,
        } as any),
      [],
    );

    const columns = useMemo<HasuraDataTableColumnDef[]>(
      () =>
        (filterColumns || ((x: HasuraDataTableColumnDef[]) => x))([
          {
            field: 'id',
            headerName: 'ID',
            width: 60,
          },
          {
            field: 'previewFileId',
            headerName: 'Превью',
            type: 'file',
            hideText: true,
            selector: 'preview { id contentType name extension }',
            width: 80,
            sortable: false,
            valueGetter({ row }) {
              return row.preview?.id;
            },
            fetchMetadata(row) {
              if (!row.preview) {
                return null;
              }

              return {
                ...row.preview,
                thumbnail: `/api/v1/files/w_100,h_100/${row.preview.id}`,
              };
            },
          },
          {
            field: 'name',
            selector: 'name',
            headerName: 'Название',
            flex: 1,
          },
          {
            field: 'type',
            headerName: 'Тип',
            flex: 1,
            type: 'select',
            items: TEMPLATE_TYPE_ITEMS,
          },
          // {
          //   field: 'inputType',
          //   headerName: 'Тип ввода',
          //   flex: 1,
          //   type: 'select',
          //   items: TEMPLATE_INPUT_TYPE_ITEMS,
          // },
          {
            field: 'tags',
            selector: 'tags { tag { name } }',
            headerName: 'Теги',
            valueGetter({ row }) {
              return row.tags.map((x: any) => x.tag.name).join(', ');
            },
            flex: 1,
          },
          {
            field: 'model',
            headerName: 'Модель',
            flex: 1,
            type: 'select',
            items: [
              ...TEMPLATE_MODEL_ITEMS.image,
              ...TEMPLATE_MODEL_ITEMS.video,
            ],
          },
          {
            field: 'isPremium',
            headerName: 'Премиум',
            flex: 1,
            type: 'boolean',
          },
          {
            field: 'isNew',
            headerName: 'Новая',
            flex: 1,
            type: 'boolean',
          },
          { field: 'createdAt', headerName: 'Дата создания', type: 'date' },
        ]),
      [filterColumns],
    );

    return (
      <DataTableEx
        {...rest}
        ref={tableRef}
        source="template"
        columns={columns}
        persistStateMode="query"
        editable={{
          onEdit(row) {
            setSelectedType(row.type);
            setSelectedTemplateId(row.id);
            tableRef.current?.openFormDialog(row);
          },
        }}
        sortBy={rest.sortBy ?? { field: 'createdAt', sort: 'desc' }}
        formTitle={(isNew) =>
          isNew
            ? `Новый шаблон-${
                selectedType === 'image' ? 'изображение' : 'видео'
              }`
            : `Редактировать шаблон-${
                selectedType === 'image' ? 'изображение' : 'видео'
              }`
        }
        formDialogProps={{
          ...rest.formDialogProps,
          maxWidth: 'md',
          formProps: {
            defaultValues: {
              ...rest.formDialogProps?.formProps?.defaultValues,
              isPremium: false,
              isNew: true,
              useImagePreprocessing: false,
              type: 'image',
              inputType: 'single',
              inputImageAmount: 1,
              imageLabels: [],
              // model: 'gen4_turbo',
              // duration: 8,
              // ratio: '720:1280',
            },
          },

          formSubmitterProps: {
            selection: ['id', 'type'],
            onSubmit(item) {
              if (needToOpenFormAfterSave.current) {
                openIdAfterSave.current = item.id;

                setTimeout(() => {
                  setSelectedTemplateId(item.id);
                  setSelectedType(item.type);
                  tableRef.current?.openFormDialog({ id: item.id });
                }, 200);
              } else {
                openIdAfterSave.current = null;
              }

              needToOpenFormAfterSave.current = false;
            },
            preSubmit(item) {
              return {
                ...item,
                type: selectedType,
                ...(!item.id && {
                  tags: {
                    data: item.tags
                      ? item.tags.map((tagId: any) => ({ tagId }))
                      : [],
                  },
                  id: undefined,
                }),
                duration: item.duration ?? 0,
              };
            },
          },
        }}
      >
        <HiddenInput name="id" />
        <HiddenInput name="type" />
        <HiddenInput name="imagePrompt" />
        <HiddenInput name="inputImageAmount" />
        <HiddenInput name="imageLabels" />

        {selectedType === 'image' ? (
          <ImageForm
            id={selectedTemplateId}
            needToOpenFormAfterSave={needToOpenFormAfterSave}
            imagesTableRef={imagesTableRef}
          />
        ) : (
          <VideoForm
            id={selectedTemplateId}
            needToOpenFormAfterSave={needToOpenFormAfterSave}
            imagesTableRef={imagesTableRef}
          />
        )}
      </DataTableEx>
    );
  },
);

function ImageForm({
  id,
  needToOpenFormAfterSave,
  imagesTableRef,
}: {
  id: number | undefined;
  needToOpenFormAfterSave: any;
  imagesTableRef: any;
}) {
  return (
    <>
      <FormInput name="name" label="Название" required md={8} />
      <CheckboxInput name="isPremium" label="Премиум" sm={2} />
      <CheckboxInput name="isNew" label="Новая" sm={2} />

      <AutocompleteInput
        name="model"
        mode="memory"
        label="Модель"
        required
        clearable={false}
        options={TEMPLATE_MODEL_ITEMS['image']}
        sm={4}
      />
      <FormGetter
        names={['model']}
        render={({ model }) => (
          <>
            <SelectInput
              name="ratio"
              label="Соотношение"
              required
              sm={4}
              items={
                !model
                  ? ([] as any)
                  : (TEMPLATE_RATIO_ITEMS[
                      model as keyof typeof TEMPLATE_RATIO_ITEMS
                    ] as any)
              }
            />
          </>
        )}
      />
      <TagsInput templateId={id} sm={4} />

      <FileInput
        name="originalImageId"
        label="Превью оригинала"
        required
        dropzoneProps={{
          accept: {
            'image/*': ['.jpg', '.jpeg', '.png', '.heic', '.heif'],
          },
          validator(file) {
            if (
              file.type !== 'image/jpeg' &&
              file.type !== 'image/png' &&
              file.type !== 'image/heic' &&
              file.type !== 'image/heif'
            ) {
              return {
                code: 'file-invalid-type',
                message: 'Поддерживаемые форматы: JPG, JPEG, PNG, HEIC, HEIF',
              };
            }

            return null;
          },
        }}
        sm={4}
      />
      <FileInput
        name="previewFileId"
        required
        label="Превью генерации"
        dropzoneProps={{
          accept: {
            'image/*': ['.jpg', '.jpeg', '.png', '.heic', '.heif'],
          },
          validator(file) {
            if (
              file.type !== 'image/jpeg' &&
              file.type !== 'image/png' &&
              file.type !== 'image/heic' &&
              file.type !== 'image/heif'
            ) {
              return {
                code: 'file-invalid-type',
                message: 'Поддерживаемые форматы: JPG, JPEG, PNG, HEIC, HEIF',
              };
            }

            return null;
          },
        }}
        sm={4}
      />
      <Grid sm={4} />
      <FormInput
        name="inputImageAmount"
        label="Число пользовательских изображений"
        type="number"
        sm={4}
        required
      />
      <AutocompleteInput
        name="imageLabels"
        label="Метки изображений"
        multiple
        freeSolo
        mode="memory"
        sm={8}
        options={[]}
        helperText="Метки можно использовать для описания изображений, которые пользователь должен загрузить"
      />
      <Grid sm={12} />
      <ImagePromptSection
        name="prompt"
        templateId={id}
        needToOpenFormAfterSave={needToOpenFormAfterSave}
        imagesTableRef={imagesTableRef}
        required
      />
    </>
  );
}

function VideoForm({
  id,
  needToOpenFormAfterSave,
  imagesTableRef,
}: {
  id: number | undefined;
  needToOpenFormAfterSave: any;
  imagesTableRef: any;
}) {
  return (
    <Fragment key={`video-form-${id}`}>
      <FormInput name="name" label="Название" required md={8} />
      <CheckboxInput name="isPremium" label="Премиум" sm={2} />
      <CheckboxInput name="isNew" label="Новая" sm={2} />

      <AutocompleteInput
        name="model"
        mode="memory"
        label="Модель"
        required
        clearable={false}
        options={TEMPLATE_MODEL_ITEMS['video']}
        sm={4}
      />

      <FormSetter
        render={(setValue) => (
          <>
            <FormGetter
              names={['model']}
              onChange={(values, previousValues) => {
                if (previousValues?.model && values?.model) {
                  const items =
                    TEMPLATE_DURATION_ITEMS[
                      values.model as keyof typeof TEMPLATE_DURATION_ITEMS
                    ];
                  if (items) {
                    setValue('duration', items[items.length - 1]);
                  }
                }
              }}
              render={({ model }) => {
                return (
                  <>
                    <SelectInput
                      name="duration"
                      label="Длительность"
                      required
                      sm={4}
                      items={
                        !model
                          ? []
                          : (
                              TEMPLATE_DURATION_ITEMS[
                                model as keyof typeof TEMPLATE_DURATION_ITEMS
                              ] ?? []
                            ).map((x) => ({
                              value: x,
                              text: x.toString(),
                            }))
                      }
                    />
                    <SelectInput
                      name="ratio"
                      label="Соотношение"
                      required
                      sm={4}
                      items={
                        !model
                          ? ([] as any)
                          : (TEMPLATE_RATIO_ITEMS[
                              model as keyof typeof TEMPLATE_RATIO_ITEMS
                            ] as any)
                      }
                    />
                  </>
                );
              }}
            />
          </>
        )}
      />

      <FileInput
        name="previewFileId"
        required
        label="Превью генерации"
        dropzoneProps={{
          accept: {
            'video/*': ['.mp4'],
          },
          validator(file) {
            if (file.type !== 'video/mp4') {
              return {
                code: 'file-invalid-type',
                message: 'Только MP4 видео поддерживаются',
              };
            }

            return null;
          },
        }}
        sm={4}
      />

      <FormInput
        name="prompt"
        label="Промпт для видео"
        rows={3}
        multiline
        required
        sm={4}
        placeholder="Опишите шаблон..."
      />
      <TagsInput templateId={id} sm={4} sx={{ minHeight: 90 }} />
      <CheckboxInput
        name="useImagePreprocessing"
        label="Использовать предварительную генерацию изображения"
        sm={4}
      />

      <FormGetter
        names={['useImagePreprocessing']}
        render={({ useImagePreprocessing }) =>
          useImagePreprocessing && (
            <>
              <FormInput
                name="inputImageAmount"
                label="Число пользовательских изображений"
                type="number"
                required
                sm={4}
              />
              <AutocompleteInput
                name="imageLabels"
                label="Метки изображений"
                multiple
                freeSolo
                mode="memory"
                sm={4}
                options={[]}
                helperText="Метки можно использовать для описания изображений, которые пользователь должен загрузить"
              />
            </>
          )
        }
      />

      <FormGetter
        names={['useImagePreprocessing']}
        render={({ useImagePreprocessing }) =>
          !useImagePreprocessing ? (
            <Grid sm={4}>
              <Box sx={{ height: 56 }} />
            </Grid>
          ) : (
            <>
              <ImagePromptSection
                name="imagePrompt"
                templateId={id}
                needToOpenFormAfterSave={needToOpenFormAfterSave}
                imagesTableRef={imagesTableRef}
              />
            </>
          )
        }
      />
    </Fragment>
  );
}

function ImagePromptSection({
  templateId,
  needToOpenFormAfterSave,
  imagesTableRef,
  name,
  required,
}: {
  templateId?: number;
  needToOpenFormAfterSave: any;
  imagesTableRef: any;
  name: string;
  required?: boolean;
}) {
  return (
    <>
      <FormInput
        name={name}
        label="Промпт для изображения"
        rows={4}
        multiline
        required={required}
        md={6}
        placeholder="Опишите шаблон..."
      />

      {!templateId ? (
        <Grid xs={12} md={6}>
          <Alert severity="warning" variant="outlined">
            <Typography>
              Для добавления изображений необходимо сохранить шаблон.
            </Typography>
            <SubmitButton
              variant="contained"
              color="primary"
              size="small"
              sx={{ ml: -1 }}
              onClick={() => {
                needToOpenFormAfterSave.current = true;
              }}
            >
              Сохранить шаблон
            </SubmitButton>
          </Alert>
        </Grid>
      ) : (
        <Grid xs={12} md={6}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              paddingX: 1,
            }}
          >
            <Typography variant="body2">Изображения</Typography>
            <IconButton
              size="small"
              onClick={() => {
                imagesTableRef.current?.openFormDialog();
              }}
            >
              <Plus />
            </IconButton>
          </Stack>

          <TemplateImagesTable
            id="template-images-table"
            ref={imagesTableRef}
            templateId={templateId}
            columnHeaderHeight={0}
            inline
            autoHeight
            editable={false}
            slots={{
              noRowsOverlay: () => (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    imagesTableRef.current?.openFormDialog();
                  }}
                >
                  Добавить изображение
                </Button>
              ),
              footer: () => null,
              columnHeaders: () => null,
            }}
            sx={{
              width: '100%',
              // minHeight: 300,
              borderColor: 'transparent',
            }}
          />
        </Grid>
      )}

      <Grid xs={12}>
        <Alert severity="info" variant="outlined">
          <Typography variant="body2">
            Изображения могут быть использованы для генерации. Для этого в
            промпте необходимо указать ссылку на изображение в формате{' '}
            <b>
              <i>@sample_1</i>
            </b>
            ,{' '}
            <b>
              <i>@sample_2</i>
            </b>
            , ..., где число - это порядковый номер изображения в таблице.
            <Divider sx={{ my: 0.5 }} />
            Пользовательские изображения также могут быть использованы для
            генерации. Для этого в промпте необходимо указать ссылку на
            изображение в формате{' '}
            <b>
              <i>@user_1</i>
            </b>
            ,{' '}
            <b>
              <i>@user_2</i>
            </b>
            <Divider sx={{ my: 0.5 }} />
            Общее число изображений, которые можно использовать для генерации,
            судя по документации, <b>не должно превышать 3</b>.
          </Typography>
        </Alert>
      </Grid>
    </>
  );
}

function TagsInput({
  templateId,
  ...props
}: { templateId?: number } & Partial<AutocompleteInputProps>) {
  return !templateId ? (
    <AutocompleteInput
      name="tags"
      label="Теги"
      source="tag"
      mode="hasura"
      selection="id name"
      itemText="name"
      itemValue="id"
      multiple
      {...(props as any)}
    />
  ) : (
    <AutocompleteInput
      name="tags"
      label="Теги"
      source="tag"
      mode="hasura"
      selection="id name"
      itemText="name"
      itemValue="id"
      multiple
      formFetcherValueResolver={{
        source: 'templateTag',
        selection: 'tagId',
        filter: { templateId: { _eq: templateId } },
        resolveItems: (items) => items.map((x) => x.tagId),
      }}
      formSubmitterValueResolver={{
        type: 'hasura-many-to-many',
        source: 'templateTag',
        foreignKey: 'tagId',
        entityId: { templateId },
      }}
      {...(props as any)}
    />
  );
}
